import { CHANNEL_REQUIRED_TYPES } from './overlayDefaults.js';

export const FRAME_ASPECT = 16 / 9;
const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

export function normalizePosition(position, type) {
  let { x, y } = position;
  let width = clamp(position.width, 0.04, 1);
  let height = clamp(position.height, 0.03, 1);
  // The export renderer draws maps square in pixels, irrespective of height.
  if (type === 'track_map') {
    width = Math.min(width, 1 / FRAME_ASPECT);
    height = width * FRAME_ASPECT;
  }
  if (type === 'gauge') {
    // Trim the old rectangular padding without enlarging or moving the dial.
    const side = clamp(Math.min(width, height / FRAME_ASPECT), 0.04, 1 / FRAME_ASPECT);
    x += (width - side) / 2;
    y += (height - side * FRAME_ASPECT) / 2;
    width = side;
    height = side * FRAME_ASPECT;
  }
  return {
    x: clamp(x, 0, 1 - width), y: clamp(y, 0, 1 - height),
    width, height,
  };
}

export function needsChannel(overlay) {
  return CHANNEL_REQUIRED_TYPES.has(overlay.type) && !overlay.channel;
}

export function serializeOverlays(overlays) {
  return overlays.map(({ _id, ...overlay }) => {
    const cleaned = Object.fromEntries(Object.entries(overlay).filter(([, value]) => value !== '' && value != null));
    // VTR requires the field for these types, but an intentionally blank label
    // is valid. Preserve it in preview, preview clips, and the final export.
    if (['numeric', 'bar', 'gauge', 'laptime'].includes(overlay.type)) cleaned.label = overlay.label ?? '';
    return cleaned;
  });
}

export function createHistory(overlays) {
  return { present: overlays, past: [], future: [], group: null, changedAt: 0 };
}

export function commitHistory(history, next, group = null, now = Date.now()) {
  if (JSON.stringify(history.present) === JSON.stringify(next)) return history;
  const merge = group != null && group === history.group && now - history.changedAt < 600;
  return {
    present: next,
    past: merge ? history.past : [...history.past, history.present].slice(-100),
    future: [], group, changedAt: now,
  };
}

export function travelHistory(history, direction) {
  if (direction === 'undo' && history.past.length) {
    return {
      present: history.past.at(-1), past: history.past.slice(0, -1),
      future: [history.present, ...history.future], group: null, changedAt: 0,
    };
  }
  if (direction === 'redo' && history.future.length) {
    return {
      present: history.future[0], past: [...history.past, history.present],
      future: history.future.slice(1), group: null, changedAt: 0,
    };
  }
  return history;
}

// Cropped renderer pixels stay attached to the component's original rectangle.
// Percentage offsets also scale the artwork immediately during a resize.
export function layerImageStyle(layer) {
  const { bounds: b, position: p, frameWidth: w, frameHeight: h } = layer;
  return {
    left: `${(b.x / w - p.x) / p.width * 100}%`,
    top: `${(b.y / h - p.y) / p.height * 100}%`,
    width: `${b.width / w / p.width * 100}%`,
    height: `${b.height / h / p.height * 100}%`,
  };
}
